import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  AbstractControl,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { AccountNumberService } from '@deltager/organizers/admin/core/services/account-number.service';
import { SettlementAccount } from '@deltager/organizers/admin/shared/models/SettlementAccount';
import { AppNotificationsService } from '@deltager/organizers/admin/core/services/app-notifications.service';
import { AffiliateService } from '@deltager/organizers/admin/core/services/affiliate.service';
import { CountryId } from '@deltager/organizers/admin/shared/models/CountryId';
import { ConfirmDialogData } from '@deltager/organizers/admin/shared/models/confirm-dialog-data.model';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '@deltager/organizers/admin/shared/dialogs/confirm-dialog/confirm-dialog.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AccountCheck } from '@deltager/organizers/admin/shared/models/AccountCheck';
import { UserService } from '@deltager/organizers/admin/core/services/user.service';
import { AccessRole, UserData } from '@deltager/organizers/admin/shared/models/LoginData';

import {
  AccountBufferEditComponent,
  AccountBufferOptions,
} from '@deltager/organizers/admin/modules/settlement-account/add-new-account/account-buffer-edit/account-buffer-edit.component';
import { SpinnerOverlayService } from '@deltager/organizers/admin/shared/spinner-overlay/spinner-overlay.service';
import { MatIconModule } from '@angular/material/icon';
import { OrganizerValidationComponent } from '@deltager/organizers/admin/modules/organizers/organizer-validation/organizer-validation.component';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DirectivesModule } from '@deltager/organizers/admin/shared/directives.module';
import { finalize, take, switchMap, filter, takeUntil, first } from 'rxjs/operators';
import { OrganizerValidationService } from '@deltager/organizers/admin/core/services/organizer-validation.service';
import { edited } from '@deltager/organizers/admin/modules/events/create-event/shared/services/event-wizard-validation-state.store';
import { BaseComponent } from '@deltager/organizers/admin/shared/components/base.component';
import { BackToListComponent } from '@deltager/organizers/admin/shared/components/back-to-list/back-to-list.component';
import { SettlementAccountOwnerType } from '@deltager/organizers/admin/modules/settlement-account/validation/shared/models/person_selection.model';
import { Store } from '@ngrx/store';
import { OrganizerSettings } from '@deltager/organizers/admin/shared/models/OrganizerSettings';
import { selectOrganizer } from '@deltager/organizers/admin/redux/root.state';

@Component({
  selector: 'app-add-new-account',
  templateUrl: './add-new-account.component.html',
  styleUrls: ['./add-new-account.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    MatIconModule,
    RouterLink,
    OrganizerValidationComponent,
    MatInputModule,
    MatCheckboxModule,
    AccountBufferEditComponent,
    DirectivesModule,
    BackToListComponent,
  ],
})
export class AddNewAccountComponent extends BaseComponent implements OnInit {
  @Input()
  isInEventSetup = false;

  @Input() onlyForm = false;

  @Output()
  accountAdded: EventEmitter<SettlementAccount> = new EventEmitter();

  @Output()
  accountUpdated: EventEmitter<void> = new EventEmitter();

  @Output() shouldMoveToValidationUrl = new EventEmitter<string>();

  @Output()
  accountCreationFailed: EventEmitter<void> = new EventEmitter();

  @Output()
  accountValidationFailed: EventEmitter<void> = new EventEmitter();

  @Input()
  eventId?: number;

  @Input()
  eventType?: number;

  ibanCountries = ['DE', 'ES', 'PL'];

  public id: number;
  public isLoading = true;
  public accessDenied = true;
  public accountFormGroup: UntypedFormGroup;
  public canSave: boolean;
  public isEditMode = false;
  public isBufferAccountActivated = false;
  public targetBufferAmount = 0;
  public currentBufferAmount = 0;
  public userData: UserData;
  public showDefaultCheckbox: boolean;
  public bufferModuleEnabled = false;
  public showGeoSpecificControlsUK: boolean;
  public needIdentityValidation = false;
  public hasSettlementAccountValidation = false;
  public needSettlementAccountValidation = false;
  public uniqueId: string;

  private _organizer: OrganizerSettings;
  private _clubId = 0;
  private _settlementAccount: SettlementAccount;
  private _accountCheck: number;
  private _updateEnabledControls: AbstractControl[] = [];
  private _geoSpecificControlsUK: AbstractControl[] = [];
  private _hasOrganisationNumber;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private accountNumberService: AccountNumberService,
    private appNotificationService: AppNotificationsService,
    private affiliateService: AffiliateService,
    private spinner: SpinnerOverlayService,
    private dialog: MatDialog,
    private router: Router,
    private translate: TranslateService,
    private userService: UserService,
    private organizerValidationService: OrganizerValidationService,
    private store: Store<{ organiser: OrganizerSettings }>,
  ) {
    super();

    this.userData = this.userService.getUserDataInstant();
    this.canSave = [AccessRole.Administrator, AccessRole.Finance].includes(this.userData.accessRole);
  }

  ngOnInit() {
    // Handle response message if returning from identity validation
    if (!this.isInEventSetup) {
      this.organizerValidationService.urlHasIdentityValidationResponse(true);
    }

    this.store
      .select(selectOrganizer)
      .pipe(
        first(o => !!o),
        takeUntil(this.destroyed$),
      )
      .subscribe(organizer => {
        this._organizer = organizer;
        this._hasOrganisationNumber = organizer.orgNumber;
        this.needIdentityValidation =
          organizer.properties.signedUpWithValidation &&
          !organizer.properties.isIdentityVerified &&
          !this._hasOrganisationNumber;

        this.bufferModuleEnabled = organizer.modules.useBufferAccount;
      });

    this.isUserAllowed();
    this.initializeEmptyForm();
    this.initForm();
  }

  public onIdentityValidationCompleted(): void {
    this.needIdentityValidation =
      this._organizer.properties.signedUpWithValidation && !this._organizer.properties.isIdentityVerified;
  }

  public onShouldMoveToValidationUrl(url: string): void {
    if (this.isInEventSetup) {
      // Let event wizard handle this
      this.shouldMoveToValidationUrl.emit(url);
      return;
    }

    // Go to validation based on identity URL
    window.location.href = url;
  }

  public OnBufferUpdated(data: AccountBufferOptions): void {
    this.isBufferAccountActivated = data.bufferAccountActivated;
    this.targetBufferAmount = data.targetBufferAmount;
  }

  public updateAccount(withSuccessNotification = true): void {
    if (!this.accountFormGroup.disabled && !this.accountFormGroup.valid) {
      return;
    }

    const accountNumber = this.accountFormGroup.get('account').value;
    let failed = false;

    const countryCode = this._organizer.countryCode;

    if (this.ibanCountries.includes(countryCode)) {
      failed =
        (countryCode === 'ES' && !this.accountNumberService.checkCountrySpecificIbanValues(accountNumber, 'ES', 24)) ||
        (countryCode === 'DE' && !this.accountNumberService.checkCountrySpecificIbanValues(accountNumber, 'DE', 22)) ||
        (countryCode === 'PL' && !this.accountNumberService.checkCountrySpecificIbanValues(accountNumber, 'PL', 28));

      if (!failed) {
        const rearranged = accountNumber.slice(4) + accountNumber.slice(0, 4);

        const converted = rearranged.replace(/[A-Z]/g, char => (char.charCodeAt(0) - 55).toString());

        let remainder = converted;

        while (remainder.length > 2) {
          const block = remainder.slice(0, 9);
          remainder = (parseInt(block, 10) % 97) + remainder.slice(block.length);
        }

        failed = parseInt(remainder, 10) % 97 !== 1;
      }
    } else {
      failed = this._accountCheck === AccountCheck.NORWAY && !this.validateAccountNumberNorway(accountNumber);
    }

    if (failed) {
      this.accountValidationFailed.emit();

      this.appNotificationService.sendError({
        title: this.translate.instant('validation.invalid.account_number'),
      });

      this.accountFormGroup.controls.account.setValue('');

      return;
    }

    let data = this.getAccountDataForSaving();

    if (this.bufferModuleEnabled) {
      data = {
        ...data,
        isBufferAccountActivated: this.isBufferAccountActivated,
        targetBufferAmount: this.targetBufferAmount,
      };
    }

    if (!this.onlyForm) {
      this.setLoading(true);
    }

    if (this.id > 0) {
      this.accountNumberService
        .updateAccountNumber(data)
        .pipe(
          take(1),
          filter(result => result),
          finalize(() => this.setLoading(false)),
          takeUntil(this.destroyed$),
        )
        .subscribe({
          next: result => {
            this.accountUpdated.emit();

            if (withSuccessNotification) {
              this.appNotificationService.sendSuccessOnUpdate(this.translate.instant('entities.account'));
            }
          },
          error: error => {
            this.appNotificationService.sendError({ title: error.message });
          },
        });
    } else {
      this.accountNumberService
        .insertAccountNumber(data)
        .pipe(
          take(1),
          finalize(() => this.setLoading(false)),
          takeUntil(this.destroyed$),
        )
        .subscribe({
          next: result => {
            if (result?.id > 0) {
              this.accountAdded.emit(result);

              this.id = result.id;

              if (withSuccessNotification) {
                this.appNotificationService.sendSuccessOnUpdate(this.translate.instant('entities.account'));
              }

              if (!this.isInEventSetup && !this.onlyForm) {
                this.router.navigate(['/organizers/accounts']);
              }
            } else {
              this.accountCreationFailed.emit();
              this.appNotificationService.sendError({
                title: this.translate.instant('validation.account_exist'),
              });

              this.accountFormGroup.controls.account.setValue('');
            }
          },
          error: error => {
            this.accountCreationFailed.emit();
            this.appNotificationService.sendError({ title: error.message });
          },
        });
    }
  }

  public deleteAccount(): void {
    this.dialog
      .open(ConfirmDialogComponent, {
        width: '450px',
        data: {
          title: this.translate.instant('settlementaccount.header_delete'),
          content: this.translate.instant('settlementaccount.delete_account_confirm'),
          isDeleteDialog: true,
          submitBtn: 'global.delete',
          cancelBtn: 'global.cancel',
        } as ConfirmDialogData,
      })
      .afterClosed()
      .pipe(
        filter(r => +r === 1),
        switchMap(() => this.accountNumberService.deleteAccountNumber(this._clubId, this.id)),
      )
      .subscribe({
        next: response => {
          if (response) {
            this.appNotificationService.sendSuccess({
              title: this.translate
                .instant('feedback.delete_success')
                .replace('$1', this._settlementAccount.accountNumber),
            });
            this.router.navigate(['/organizers/accounts']);
          } else {
            this.appNotificationService.sendErrorGeneric();
          }
        },
        error: error => {
          this.appNotificationService.sendError({ title: error.message });
        },
      });
  }

  public noWhitespaceValidator(control: UntypedFormControl) {
    const isWhitespace = (control.value || '').trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : { whitespace: true };
  }

  private async initForm(): Promise<void> {
    this.getIdFromUrl();
    this.getAffSettings();

    if (this.id > 0) {
      this.isEditMode = true;
      this.getPreloadedData();
      this.disableControls(this.accountFormGroup, this._updateEnabledControls);
    } else {
      this.setLoading(false);
    }
  }

  private getIdFromUrl(): void {
    this.id = +this.route.snapshot.paramMap.get('id');
    // this.clubid = +this.route.snapshot.paramMap.get("clubid");
  }

  private initializeEmptyForm(): void {
    this.accountFormGroup = this.formBuilder.group({
      name: ['', [Validators.required, this.noWhitespaceValidator, Validators.maxLength(150)]],
      sortcode: [''],
      account: [''],
      status: [''],
      isDefault: [''],
    });
    this._geoSpecificControlsUK.push(this.accountFormGroup.get('sortcode'));
  }

  private getPreloadedData(): void {
    this.setLoading(true);

    this.accountNumberService
      .getSettlementAccount(this.userService.OrganizerId, this.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: result => {
          if (result != null) {
            this.accountFormGroup.patchValue({
              name: result.fullName,
              sortcode: result.sortcode,
              account: result.accountNumber,
              status: result.status,
              isDefault: result.isDefault,
            });

            this._settlementAccount = result;
            this.showDefaultCheckbox = result.isDefault;
            this.setLoading(false);
            this.isBufferAccountActivated = result.isBufferAccountActivated;
            this.targetBufferAmount = result.targetBufferAmount;
            this.currentBufferAmount = result.currentBufferAmount;
            this.uniqueId = result.uniqueId;

            this.hasSettlementAccountValidation =
              this.affiliateService.settings.validateAccounts &&
              this._organizer.orgNumber.length > 5 &&
              this._organizer.properties.signedUpWithValidation;

            this.needSettlementAccountValidation = !this._settlementAccount.isValidated;
          } else {
            this.appNotificationService.sendError({
              title: this.translate.instant('validation.no_data'),
            });
            this.router.navigate(['/organizers/accounts']);
          }
        },
        error: error => this.appNotificationService.sendErrorGeneric(),
      });
  }

  private getAffSettings(): void {
    const affSettings = this.affiliateService.settings;

    this.showGeoSpecificControlsUK =
      (!this._organizer.countryCode && affSettings.countryId === CountryId.UK) || this._organizer.countryCode == 'GB';

    this._accountCheck = affSettings.accountCheck;

    this.resetControlsByAccountCheck();
    this.resetControlsByGeoSpecific();
  }

  private validateAccountNumberNorway(accountNumber: string): boolean {
    return this.accountNumberService.checkAccountNumberNORWAY(accountNumber);
  }

  private resetFormGroup(group: UntypedFormGroup, controls: AbstractControl[]): void {
    Object.keys(group.controls).forEach((key: string) => {
      const abstractControl = group.controls[key] as AbstractControl;
      if (controls.indexOf(abstractControl) > -1) {
        abstractControl.setValidators(null);
        abstractControl.setErrors(null);
      }
    });
  }

  private disableControls(group: UntypedFormGroup, exceptions: AbstractControl[]): void {
    Object.keys(group.controls).forEach((key: string) => {
      const abstractControl = group.controls[key] as AbstractControl;
      if (exceptions.indexOf(abstractControl) === -1) {
        abstractControl.disable({ onlySelf: true });
      }
    });
  }

  private getAccountDataForSaving(): SettlementAccount {
    return {
      id: this.id,
      organizerId: this._clubId,
      sortcode: this.accountFormGroup.get('sortcode').value,
      accountNumber: this.accountFormGroup.get('account').value,
      fullName: this.accountFormGroup.get('name').value,
      eventId: this.eventId,
      eventType: this.eventType,
    } as SettlementAccount;
  }

  private resetControlsByAccountCheck(): void {
    if (this._organizer.countryCode) {
      this.accountFormGroup.controls.account.setValidators([Validators.required]);

      if (this._organizer.countryCode === 'GB') {
        this.accountFormGroup.controls.account.setValidators([
          Validators.required,
          Validators.pattern(/^[0-9]+$/),
          Validators.maxLength(8),
          Validators.minLength(7),
        ]);

        this.accountFormGroup.controls.sortcode.setValidators([
          Validators.required,
          Validators.pattern(/^[0-9]+$/),
          Validators.maxLength(6),
          Validators.minLength(6),
        ]);
      }
    } else {
      if (this._accountCheck === AccountCheck.UK) {
        this.accountFormGroup.controls.account.setValidators([
          Validators.required,
          Validators.pattern(/^[0-9]+$/),
          Validators.maxLength(8),
          Validators.minLength(7),
        ]);
        this.accountFormGroup.controls.sortcode.setValidators([
          Validators.required,
          Validators.pattern(/^[0-9]+$/),
          Validators.maxLength(6),
          Validators.minLength(6),
        ]);
      } else if (this._accountCheck === AccountCheck.US) {
        this.accountFormGroup.controls.account.setValidators([
          Validators.required,
          Validators.pattern(/^[0-9]+$/),
          Validators.maxLength(12),
          Validators.minLength(8),
        ]);
        this.accountFormGroup.controls.sortcode.setValidators([
          Validators.required,
          Validators.pattern(/^[0-9]+$/),
          Validators.maxLength(9),
          Validators.minLength(9),
        ]);
      } else if (this._accountCheck === AccountCheck.NORWAY) {
        this.accountFormGroup.controls.account.setValidators([
          Validators.required,
          Validators.pattern(/^[0-9]+$/),
          Validators.maxLength(11),
          Validators.minLength(11),
        ]);
      }
    }
  }

  private resetControlsByGeoSpecific(): void {
    if (!this.showGeoSpecificControlsUK) {
      this.resetFormGroup(this.accountFormGroup, this._geoSpecificControlsUK);
    } else {
      this.accountFormGroup.controls.sortcode.setValidators([
        Validators.required,
        Validators.pattern(/^[0-9]+$/),
        Validators.maxLength(6),
        Validators.minLength(6),
      ]);
    }
  }

  private setLoading(loading: boolean): void {
    if (loading) {
      this.spinner.show();
    } else {
      this.spinner.hide();
    }

    this.isLoading = loading;
  }

  private async isUserAllowed(): Promise<void> {
    const result = await this.userService.getUserData();
    this._clubId = result.clubId;
    if (result.isAdministrator() || result.accessRole === AccessRole.Finance) {
      this.accessDenied = false;
    } else {
      this.accessDenied = true;
    }
  }

  protected readonly edited = edited;
  protected readonly SettlementAccountOwnerType = SettlementAccountOwnerType;
}
